// src/components/ReviewsPage.js
import React, { useState, useEffect } from 'react';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './ReviewsPage.css';

const ReviewsPage = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showZillowIframe, setShowZillowIframe] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.height = '';
    };
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`https://tophamtechbe.com/api/treas/reviews/v1/get-review?site_id=${SITE_CONFIG.SITE_ID}`, {
        headers: {
          'X-API-Key': 'treas-api-key-2025'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReviews(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/reviews/v1/store-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          review_text: reviewText
        })
      });

      if (response.ok) {
        navigator.clipboard.writeText(reviewText);
        window.open(SITE_CONFIG.AGENT.ZILLOW_REVIEW, '_blank');

        setShowCreateForm(false);
        setShowZillowIframe(true);
        fetchReviews();
      } else {
        alert('Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`reviews-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="reviews-header">
        <div>
          <h1>Customer Reviews & Testimonials</h1>
          <p>Read feedback from clients who worked with {SITE_CONFIG.AGENT.NAME} on their real estate goals.</p>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowCreateForm(true)} 
            className="write-review-btn"
          >
            Write a Review
          </button>
          <button className="close-reviews-btn" onClick={onClose}>
            ← Back
          </button>
        </div>
      </div>
      
      <div className="reviews-container">
        <div className="reviews-grid">
          {loading ? (
            <p>Loading reviews...</p>
          ) : reviews.length === 0 ? (
            <div className="no-reviews">
              <h3>No reviews yet</h3>
              <p>Be the first to leave a review!</p>
            </div>
          ) : (
            reviews.map((review, index) => (
              <div key={index} className="review-card">
                <div className="review-stars">⭐⭐⭐⭐⭐</div>
                <p className="review-text">{review.review_text}</p>
                <p className="review-date">
                  {new Date(review.created_at || Date.now()).toLocaleDateString()}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {showCreateForm && (
        <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Write a Review</h3>
            <p className="anonymous-note">
              Reviews are anonymous. If you wish to reveal your identity, please include your name in the message.
            </p>
            <form onSubmit={handleSubmitReview}>
              <textarea
                placeholder="Share your experience..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows="6"
                required
              />
              <div className="modal-buttons">
                <button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Next'}
                </button>
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {showZillowIframe && (
        <div className="modal-overlay" onClick={() => setShowZillowIframe(false)}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✅</div>
            <h3>Review Submitted Successfully!</h3>
            <p>Your review text has been copied to your clipboard. Click below to paste it on your preferred platform:</p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px', alignItems: 'center'}}>
              <button 
                className="success-close-btn"
                onClick={() => window.open(SITE_CONFIG.AGENT.ZILLOW_REVIEW, '_blank')}
              >
                Zillow Review
              </button>
              {SITE_CONFIG.AGENT.GOOGLE_REVIEW && <button 
                className="success-close-btn"
                onClick={() => window.open(SITE_CONFIG.AGENT.GOOGLE_REVIEW, '_blank')}
              >
                Google Review
              </button>}
            </div>
            <button 
              className="success-close-btn"
              onClick={() => setShowZillowIframe(false)}
              style={{marginTop: '20px', background: '#333', border: '1px solid #333'}}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;
